import { Injectable } from "@angular/core";
import {
  HttpClient,
  HttpHeaders,
  HttpErrorResponse,
} from "@angular/common/http";
import { Observable } from "rxjs/Observable";
import { map } from "rxjs/operators/map";
import { Router } from "@angular/router";
import { environment } from "../../environments/environment";

/*
    Fields to save user details into database
 */
export interface UserPayload {
  name?: String;
  short_name?: String;
  userid?: Number;
  email?: String;
  is_api_client?: String;
  phone?: Number;
  password?: String;
  passwordrepeat?: String;
  dob?: Date;
  address?: String;
  city?: String;
  state?: String;
  country?: String;
  postalcode?: Number;
  email_verified?: Number;
  phone_verified?: Number;
  group?: String;
  gender?: String;
  secret?: String;
  _csrf?: String;
  _id?: String;
  full_phone?: Number;
  liquidity?: Object;
  phonecode?: Number;
  eurbalance?: Number;
  referal_code?: String;
}

/*
    Fields to save fiat deposit details into database
 */
export interface fiatPayload {
  name?: String;
  currency?: String;
  userid?: String;
  amount?: Number;
  bankrefid?: String;
  withcurrency?: String;
  withuserid?: Number;
  withamount?: Number;
  withbankrefid?: String;
  _csrf?: String;
  currency_type?: String;
  transaction_date?: String;
  note?: String;
}

/*
    specific format of create user call reponse
 */
export interface BankPayload {
  _id?: string;
  userId?: String;
  email?: String;
  bankname?: String;
  accountname?: String;
  accountnumber?: Number;
  swiftcode?: String;
  ifsccode?: String;
  address?: String;
  city?: String;
  state?: String;
  country?: String;
  pincode?: String;
  bankaddress?: String;
  status?: Boolean;
  authflag: Boolean;
  _csrf: String;
}

export interface UserDetails {
  _id: string;
  email: string;
  name: string;
  userid: number;
  group: string;
  exp: number;
  iat: number;
}

/*
    interface for token
 */
interface TokenResponse {
  token: string;
}

/*
    Fields to save user details into database
 */
export interface TokenPayload {
  email?: String;
  password?: String;
  authflag: Boolean;
  _csrf: String;
  captcha?: String;
}

export interface TfaLoginPayload {
  email?: String;
  password?: String;
  code?: String;
  _csrf: String;
  captcha?: String;
}

export interface kycupload {
  _csrf: String;
  front?: String;
  back?: String;
  pan?: String;
  company_reg?: String;
  authflag: Boolean;
  board_member_id: String;
  residprooffront?: String;
  residproofback?: String;
  resaddressproof?: String;
  resphotoproof?: String;
  aoa?: String;
  bank_accn?: String;
  video_selfie?: String;
  source_funds?: String;
  appln_questionnaire?: String;
  addn_infor?: String;
  userid?: String;
  letter_of_auth?: String;
}

@Injectable()
export class UserService {
  /*
        back end application api url
     */
  api_url: string = environment.apiEndPoint;

  constructor(private http: HttpClient, private router: Router) { }
  /*
        save token into localStorage as a item with specific key
     */
  private saveToken(token: string): void {
    localStorage.setItem(environment.TOKEN_SECRET, token);
  }
  /*
        call for fetch token from localStrogae
     */
  private getToken(): string {
    return localStorage.getItem(environment.TOKEN_SECRET);
  }
  /*
        call for fetch toket from localStrogae
     */
  public getTkn(): string {
    return localStorage.getItem(environment.TOKEN_SECRET);
  }

  /*
        fetch user token details
     */
  public getUserDetails(): UserDetails {
    const token = this.getToken();
    let payload;
    if (token) {
      payload = token.split(".")[1];
      payload = window.atob(payload);
      return JSON.parse(payload);
    } else {
      return null;
    }
  }
  /*
        call for check the user session
     */
  public isLoggedIn(): boolean {
    const user = this.getUserDetails();
    if (user) {
      return user.exp > Date.now() / 1000;
    } else {
      return false;
    }
  }
  /*
        all type of api call handlers at client side and send token in header in all GET api call to verify valid user
        application at back end match user token with this token, if both token are match means this is a valid user otherwise
        return with a exception invalid user
     */
  public request(
    method: "post" | "get",
    type,
    user?: TokenPayload | TfaLoginPayload,
    paramslist?
  ): Observable<any> {
    let base;
    if (method === "post") {
      if (type === "login" || type === "admintfalogin") {
        base = this.http.post<any>(this.api_url + type, user, {
          withCredentials: true,
        });
      } else {
        base = this.http.post<any>(this.api_url + type, user, {
          headers: { Authorization: `Bearer ${this.getToken()}` },
          withCredentials: true,
        });
      }
    } else {
      base = this.http.get<any>(this.api_url + type, {
        headers: { Authorization: `Bearer ${this.getToken()}` },
        params: paramslist,
        withCredentials: true,
      });
    }
    const request = base.pipe(
      map((data: TokenResponse) => {
        if (data !== null && data.token) {
          this.saveToken(data.token);
        }
        return data;
      })
    );

    return request;
  }

  // Unused endpoint - not called anywhere in the app
  // public getBlocksizeReq(cur): Observable<any> {
  //   return this.http.get<any>(this.api_url + "blocksizeReq/" + cur, {
  //     withCredentials: true,
  //   });
  // }

  /* User Request */

  public userequest(
    method: "post" | "get",
    type,
    user?,
    paramslist?
  ): Observable<any> {
    if (method === "post") {
      return this.http.post<any>(this.api_url + type, user, {
        withCredentials: true,
        headers: { Authorization: `Bearer ${this.getToken()}` },
      });
    } else {
      return this.http.get<any>(this.api_url + type, {
        headers: { Authorization: `Bearer ${this.getToken()}` },
        withCredentials: true,
        params: paramslist,
      });
    }
  }
  public timezone(): Observable<any> {
    return this.http.get<any>("assets/json/timezone.json");
  }
  /*
        api call using post mether for csrf
     */
  public csrf(): Observable<any> {
    return this.http.get<any>(this.api_url + "csrf", { withCredentials: true });
  }

  /*
        api call for login user
     */
  public login(user: TokenPayload): Observable<any> {
    return this.request("post", "adminlogin", user);
  }
  /*
        api call for step 2 of login when the account has authenticator 2FA
        enabled - adminlogin responds with tfaRequired instead of a token,
        and this exchanges the OTP code (plus the same credentials) for one
     */
  public tfaLogin(payload: TfaLoginPayload): Observable<any> {
    return this.request("post", "admintfalogin", payload);
  }
  /*
      api call for add new customer
   */
  public addNewCustomer(user: UserPayload): Observable<any> {
    return this.userequest("post", "registrationAdm", user);
  }
  public getPositionBooks(filter): Observable<any> {
    return this.userequest("post", "positionbook", filter);
  }

  public createWalletByUser(id): Observable<any> {
    return this.userequest("get", "createWalletByUser/" + id);
  }
  public getInternalPair(): Observable<any> {
    return this.userequest("get", "currencies");
  }
  public getExchanges(): Observable<any> {
    return this.userequest("get", "exchanges");
  }
  public getClients(): Observable<any> {
    return this.userequest("get", "get_clients");
  }
  public saveClientTrade(obj): Observable<any> {
    return this.userequest("post", "save_manual_client_trade", obj);
  }
  public getVault(id): Observable<any> {
    return this.userequest("get", "getvault/" + id);
  }

  public getCwalUser(id): Observable<any> {
    return this.userequest("get", "getCwalUser/" + id);
  }
  public getClientAssetReport(filter: any): Observable<any> {
    return this.userequest("post", "getClientAssetReport", filter);
  }

  public getinTouch(): Observable<any> {
    return this.userequest("get", "getintouch");
  }

  public addBalance(user: fiatPayload): Observable<any> {
    return this.userequest("post", "addBalance", user);
  }

  public reduceBalance(user: fiatPayload): Observable<any> {
    return this.userequest("post", "reduceBalance", user);
  }

  public updateUserbyAdmin(user): Observable<any> {
    return this.userequest("post", "updateuserbyadmin", user);
  }
  public updateCMS(user): Observable<any> {
    return this.userequest("post", "updatecms", user);
  }
  public updateCMSStatus(user): Observable<any> {
    return this.userequest("post", "updatecmsstatus", user);
  }
  public country(): Observable<any> {
    return this.userequest("get", "countrylistadmin");
    // return this.http.get<any>("get",'countrylist');
  }
  // Unused endpoint - not called anywhere in the app
  // public generateWallet(customerid, _csrf): Observable<any> {
  //   var passdata = { customerid: customerid, _csrf: _csrf };
  //   return this.userequest("post", "generateWallet", passdata);
  // }
  /**
   * api call for disable tfa login
   * @param req
   */
  public updateTfabyAdmin(req): Observable<any> {
    return this.userequest("post", "updateTfabyadmin", req);
  }
  /*
        logout the user and destroy the session of user
     */
  public logout(): void {
    // Best-effort server-side session revocation (rotates session_id so the
    // token being discarded here stops working immediately instead of
    // staying valid until it naturally expires) - fired while the token is
    // still in storage, but local logout proceeds regardless of the result.
    this.userequest("post", "logout", {}).subscribe(
      () => {},
      () => {}
    );
    window.localStorage.removeItem(environment.TOKEN_SECRET);
    window.localStorage.removeItem("permission");
    this.router.navigateByUrl("/index");
  }
  /*
        api call for get customer list
     */
  public getcustomerList(perpage, page, search, csrf, status): Observable<any> {
    return this.userequest("post", "customer", {
      perpage: perpage,
      page: page,
      search: search,
      _csrf: csrf,
      status: status,
    });
  }
  public getCustomerList(filter): Observable<any> {
    return this.userequest("post", "customer", filter);
  }
  public getCustomers(csrf): Observable<any> {
    return this.userequest("get", "customerlist", { _csrf: csrf });
  }
  public getAdminParentMenus(): Observable<any> {
    return this.userequest("get", "admin-parent-menu");
  }
  public saveSubmenu(data): Observable<any> {
    return this.userequest("post", "admin-sub-menu", data);
  }
  public getAllSumsubAudit(filter: any): Observable<any> {
    return this.userequest("post", "getAllSumsubAudit", filter);
  }
  public getAllAMLBotAudit(filter: any): Observable<any> {
    return this.userequest("post", "getAllAMLBotAudit", filter);
  }
  public getAllHypevergeAudit(filter: any): Observable<any> {
    return this.userequest("post", "getAllHypevergeAudit", filter);
  }
  public getGroups(): Observable<any> {
    return this.userequest("get", "groups");
  }
  /**
   *
   * @param csrf
   * @returns
   */
  public saveTradeReport(obj): Observable<any> {
    return this.userequest("post", "savetradereport", obj);
  }
  /*
   * api call for active customer */
  public getCustomerDetailsById(userId): Observable<any> {
    return this.request("get", "customerbyid", null, { id: userId });
  }
  /*
   * loads the logged-in admin's own profile - separate from
   * getCustomerDetailsById, which loads an arbitrary customer's profile */
  public getAdminProfileById(adminId): Observable<any> {
    return this.request("get", "adminprofilebyid", null, { id: adminId });
  }

  /*
   * api call for active customer */
  public setActiveLatest(obj): Observable<any> {
    return this.userequest("post", "custstatusupdate", obj);
  }
  // public updateCustomerWithdrawStatus(obj):Observable<any>{
  //     return this.userequest('post','cus_withdraw_status_update', obj);
  // }
  public updateCustomerName(obj): Observable<any> {
    return this.userequest("post", "cus_name_update", obj);
  }

  public updateCustomer(obj): Observable<any> {
    return this.userequest("post", "customer_update", obj);
  }

  public setActive(userId): Observable<any> {
    return this.request("get", "custstatusupdate", null, {
      id: userId,
      status: "Active",
    });
  }

  /*
   * api call for deactive customer */
  // Unused endpoint - not called anywhere in the app
  // public setDeactive(userId): Observable<any> {
  //   return this.request("get", "custstatusupdate", null, {
  //     id: userId,
  //     status: "Deactive",
  //   });
  // }

  /*
   * api call for delete customer */
  public customerDelete(userId): Observable<any> {
    return this.request("get", "deletecustomer", null, { id: userId });
  }

  /*
   * api call for delete customer */
  public getLoginActivityList(obj): Observable<any> {
    return this.request("get", "getloginlogs", null, obj);
  }
  public getLoginAllActivityList(obj): Observable<any> {
    return this.request("get", "getloginAllActivity", null, obj);
  }

  public resetPassword(req): Observable<any> {
    return this.userequest("post", "setforgetpasswordal", req);
  }
  public getMobileCode(id): Observable<any> {
    return this.userequest("post", "adminmobilecode", id);
  }
  public checkPasswordLink(data): Observable<any> {
    return this.request("post", "checkpasswordgeneration", data);
  }
  public verifyAdminDevice(str): Observable<any> {
    // Client ip is attached to every request by ClientIpInterceptor as a header.
    return this.request("get", "adminverifydevice", null, { str: str });
  }
  public setPassword(data): Observable<any> {
    return this.userequest("post", "setpassword", data);
  }

  // Unused endpoint - not called anywhere in the app
  // public getbasis_id(): Observable<any> {
  //   return this.request("get", "getbasis_id");
  // }

  public uploadDocn(fileToUpload: File, key, csrf): Observable<any> {
    const formData = new FormData();
    formData.append(key, fileToUpload);
    return this.userequest(
      "post",
      "uploaddocument" + "?_csrf=" + csrf,
      formData
    );
  }

  public kycSave(doc: kycupload): Observable<any> {
    return this.userequest("post", "kycupdatebyadmin", doc);
  }

  // Kyc save draft
  public savedraftKyc(doc: kycupload): Observable<any> {
    return this.userequest("post", "savedraftKycbyadmin", doc);
  }

  public getLedgerList(filter): Observable<any> {
    return this.userequest("post", "getledgerlistbyuseridadmin", filter);
  }
  public getAllCurrencies(type): Observable<any> {
    return this.userequest("get", "getcurrencylist", null, { type: type });
  }
  public getAllCurrency(): Observable<any> {
    return this.userequest("get", "getcurrencies");
  }
  public saveExchangeWithdraw(data: any): Observable<any> {
    return this.userequest("post", "save-exchange-withdraw", data);
  }
  public getExchangeAsset(): Observable<any> {
    return this.userequest("get", "getExchangeAsset");
  }
  public getMarketPrices(filter): Observable<any> {
    return this.userequest("post", "admin-marketprices", filter);
  }
  public addEditMarketPrice(filter): Observable<any> {
    return this.userequest("post", "add-edit-marketprices", filter);
  }
  public getBanks(): Observable<any> {
    return this.userequest("get", "getbanks");
  }
  public getUserForDropdown(): Observable<any> {
    return this.userequest("get", "getUserForDropdown");
  }
  public getUserBank(id): Observable<any> {
    return this.userequest("get", "getUserBank/" + id);
  }
  public saveUserBank(data: any): Observable<any> {
    return this.userequest("post", "saveUserBank", data);
  }
  public getPayouts(data: any): Observable<any> {
    return this.userequest("post", "payout/getAllRequests", data);
  }
  public getPayoutRows(data: any): Observable<any> {
    return this.userequest("get", `payout/getRequestRowsById`, null, data);
  }
  public approveOrReject(data: any): Observable<any> {
    return this.userequest("post", "payout/approveOrReject", data);
  }

  //Payout Bulk Transfers
  public getAllPayoutSettlements(data: any): Observable<any> {
    return this.userequest("post", "payout/getAllPayoutSettlements", data);
  }


  //Payout Topup
  public getAllPayoutTopup(data: any): Observable<any> {
    return this.userequest("post", "payout/getAllPayoutTopup", data);
  }

  public getPayoutTopupSummary(data: any): Observable<any> {
    return this.userequest("post", "payout/getPayoutTopupSummary", data);
  }

  //Payout SettlementSummary
  public getAllPayoutSettlementSummary(data: any): Observable<any> {
    return this.userequest("post", "payout/getAllPayoutSettlementSummary", data);
  }

  public createPayoutTopup(data: any): Observable<any> {
    return this.userequest("post", "payout/createPyaoutTopup", data);
  }

  public updatePayoutTopup(data: any): Observable<any> {
    return this.userequest("post", "payout/updatePayoutTopup", data);
  }

  //Payout Wallet / Ledger
  public getAllPayoutWallets(data: any): Observable<any> {
    return this.userequest("post", "payout/getAllPayoutWallets", data);
  }

  public getAllPayoutLedger(data: any): Observable<any> {
    return this.userequest("post", "payout/getAllPayoutLedger", data);
  }

  public getPayoutLedgerSummary(data: any): Observable<any> {
    return this.userequest("post", "payout/getPayoutLedgerSummary", data);
  }

  public createPayoutWalletAdjustment(data: any): Observable<any> {
    return this.userequest("post", "payout/createPayoutWalletAdjustment", data);
  }

  public getPayoutWalletAdjustmentStatus(data: any): Observable<any> {
    return this.userequest("post", "payout/getPayoutWalletAdjustmentStatus", data);
  }

  //Gateway Wallet / Ledger (per gateway + MID)
  public getGatewayWallets(): Observable<any> {
    return this.userequest("get", "payout/getGatewayWallets");
  }

  public getGatewayLedger(data: any): Observable<any> {
    return this.userequest("post", "payout/getGatewayLedger", data);
  }

  public getGatewayLedgerSummary(data: any): Observable<any> {
    return this.userequest("post", "payout/getGatewayLedgerSummary", data);
  }

  public createGatewayWalletAdjustment(data: any): Observable<any> {
    return this.userequest("post", "payout/createGatewayWalletAdjustment", data);
  }

  public getGatewayWalletAdjustmentStatus(data: any): Observable<any> {
    return this.userequest("post", "payout/getGatewayWalletAdjustmentStatus", data);
  }

  //Gateway Deposits
  public getGatewayDeposits(data: any): Observable<any> {
    return this.userequest("post", "payout/getGatewayDeposits", data);
  }

  public createGatewayDeposit(data: any): Observable<any> {
    return this.userequest("post", "payout/createGatewayDeposit", data);
  }

  public updateGatewayDeposit(data: any): Observable<any> {
    return this.userequest("post", "payout/updateGatewayDeposit", data);
  }

  //Payout Customers
  public getPayoutCustomers(data: any): Observable<any> {
    return this.userequest("post", "payout/getAllPayoutCustomers", data);
  }

  //Payout Quotes
  public getPayoutQuotes(data: any): Observable<any> {
    return this.userequest("post", "payout/getAllQuoteRequests", data);
  }

  //Webhook Responses
  public getWebhookResponses(data: any): Observable<any> {
    return this.userequest("post", "payout/getAllWebhookResponses", data);
  }

  //Payout Callback
  public getPayoutCallbacks(data: any): Observable<any> {
    return this.userequest("post", "payout/getAllPayoutCallbacks", data);
  }

  //Payout Orders
  public getPayoutOrders(data: any): Observable<any> {
    return this.userequest("post", "payout/getAllPayoutOrder", data);
  }

  public getPayoutOrderCount(data: any): Observable<any> {
    return this.userequest("post", "payout/getPayoutOrderCount", data);
  }

  public getPayoutOrderErrorReport(data: any): Observable<any> {
    return this.userequest("post", "payout/getPayoutOrderErrorReport", data);
  }

  public exportPayoutOrders(data: any): Observable<any> {
    return this.userequest("post", "payout/exportAllPayoutOrder", data);
  }


  public pyaoutOrderApproveReject(data: any): Observable<any> {
    return this.userequest("post", "payout/pyaoutOrderApprove", data);
  }

  public updatePayoutOrder(data: any): Observable<any> {
    return this.userequest("post", "payout/updatePayoutOrder", data);
  }

  public createPayoutOrder(data: any): Observable<any> {
    return this.userequest("post", "payout/createPayoutOrder", data);
  }

  // Payout Report Export
  public exportPayoutReport(data: any): Observable<any> {
    return this.userequest("post", "payout/exportPayoutReport", data);
  }

  public listPayoutReports(): Observable<any> {
    return this.userequest("get", "payout/listPayoutReports", {});
  }

  public listAllS3Reports(): Observable<any> {
    return this.userequest("get", "payout/listAllS3Reports", null);
  }

  public downloadPayoutReport(key: string): Observable<any> {
    // Send key as query parameter
    return this.userequest("get", "payout/downloadPayoutReport", null, { key: key });
  }
}



// WEBPACK FOOTER //
// ./src/app/services/user.service.ts